import { CustomTooltip } from 'app/components/Common/Tooltips';
import { XGridChipCell } from 'app/components/Common/XGrid/cells/XGridChipCell';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { XGridOverflowCell } from 'app/components/Common/XGrid/cells/XGridOverflowCell';
import { xGridDateSort, xGridNumberSort } from 'app/components/Common/XGrid/sorting/XGridSorting';
import {
  CandidatePacketStatus,
  ClientConfirmationStatus,
  PlacementDocumentSource,
  PlacementDocumentStatus,
  PlacementDocumentType,
} from 'store/redux-store/placement-documents/types';
import React from 'react';
import { DocumentIconCell } from './GridCells/DocumentIconCell';
import PassportIcon from 'app/assets/images/CandidateHeader/Passport-enabled.svg';
import { GridCellParams, GridColumns } from '@mui/x-data-grid-pro';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import { XGridHoverBlueCell } from '@AMIEWEB/Common/XGrid/cells/XGridHoverBlueCell';
import { DocumentsXGridHoverView } from '@AMIEWEB/Common/XGrid/cells/HoverViewRenderers/DocumentsXGridHoverView';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import moment from 'moment';
import { missingField } from 'app/constants';
import { DocumentsXGridOverflowCell } from '@AMIEWEB/Common/XGrid/cells/DocumentsXGridOverflowCell';
import { stringMatches } from 'utils/string/string';
import { ContractDocSource, ContractStatus } from 'store/redux-store/placement-candidate-contract-doc-editor/types';
import { Grid, Typography, useTheme } from '@mui/material';
import { customSwitch } from 'utils/customSwitchCase/customSwitch';
import { useTranslation } from 'react-i18next';
import { IPlacementStatusRequirementTooltip } from 'app/models/Placement/PlacementDetails';

const getColumnDefinition = (t, key) => {
  return {
    ...XGridDefaultColumn,
    field: key,
    headerName: t(`placement.profile.tabs.documentTab.columns.${key}`),
  };
};

const canShowPopper = (row, value) => {
  return (
    (stringMatches(row.document, PlacementDocumentType.clientConfirmation) ||
      stringMatches(row.document, PlacementDocumentType.candidatePacket)) &&
    row?.emailLogs?.length > 0 &&
    value !== missingField
  );
};

const getClassicContractStatusColor = theme =>
  customSwitch({
    [ContractStatus.pending]: `${theme.palette.system.suvaGrey}`,
    [ContractStatus.requestContract]: `${theme.palette.system.suvaGrey}`,
    [ContractStatus.okToSend]: `${theme.palette.system.warmOrange}`,
    [ContractStatus.created]: `${theme.palette.system.warmOrange}`,
    [ContractStatus.published]: `${theme.palette.system.warmOrange}`,
    [ContractStatus.signed]: `${theme.palette.framework.system.kellyGreen}`,
  })(`${theme.palette.system.suvaGrey}`);

const getWebContractStatusColor = theme =>
  customSwitch({
    [ContractStatus.created]: `${theme.palette.system.warmOrange}`,
    [ContractStatus.published]: `${theme.palette.system.warmOrange}`,
    [ContractStatus.publishedVoid]: `${theme.palette.system.suvaGrey}`,
    [ContractStatus.signed]: `${theme.palette.framework.system.kellyGreen}`,
  })(`${theme.palette.system.suvaGrey}`);

const getPacketStatusColor = theme =>
  customSwitch({
    [CandidatePacketStatus.okToSend]: `${theme.palette.system.warmOrange}`,
    [CandidatePacketStatus.sent]: `${theme.palette.framework.system.kellyGreen}`,
  })(`${theme.palette.system.suvaGrey}`);
const getWageRequestStatusColor = theme =>
  customSwitch({
    [PlacementDocumentStatus.signed]: `${theme.palette.framework.system.kellyGreen}`,
    [PlacementDocumentStatus.sent]: `${theme.palette.system.warmOrange}`,
  })(`${theme.palette.system.suvaGrey}`);

const getClassicConfirmationStatusColor = theme =>
  customSwitch({
    [ClientConfirmationStatus.okToSend]: `${theme.palette.system.warmOrange}`,
    [CandidatePacketStatus.sent]: `${theme.palette.framework.system.kellyGreen}`,
  })(`${theme.palette.system.suvaGrey}`);

const getWebConfirmationStatusColor = theme =>
  customSwitch({
    [CandidatePacketStatus.sent]: `${theme.palette.framework.system.kellyGreen}`,
  })(`${theme.palette.system.suvaGrey}`);

const getStatusColor = doc => {
  const theme = useTheme();
  const status = doc.displayStatus ?? doc.status;
  if (stringMatches(doc.document, PlacementDocumentType.contract)) {
    if (doc.source === ContractDocSource.amieClassic) {
      return getClassicContractStatusColor(theme)(status);
    } else if (doc.source === ContractDocSource.amieWeb) {
      return getWebContractStatusColor(theme)(status);
    } else return `${theme.palette.system.suvaGrey}`;
  } else if (stringMatches(doc.document, PlacementDocumentType.candidatePacket)) {
    return getPacketStatusColor(theme)(status);
  } else if (stringMatches(doc.document, PlacementDocumentType.wageRequestForm)) {
    return getWageRequestStatusColor(theme)(status);
  } else if (stringMatches(doc.document, PlacementDocumentType.clientConfirmation)) {
    if (doc.source === ContractDocSource.amieClassic) {
      return getClassicConfirmationStatusColor(theme)(status);
    } else if (doc.source === ContractDocSource.amieWeb) {
      return getWebConfirmationStatusColor(theme)(status);
    }
  } else return `${theme.palette.framework.system.kellyGreen}`;
};

const displayIcon = (t, rowData) => {
  if (
    stringMatches(rowData.document, PlacementDocumentType.contract) &&
    ((rowData.isTsc && rowData.source === ContractDocSource.amieClassic) ||
      (rowData.source === ContractDocSource.amieWeb &&
        (rowData.status === ContractStatus.published || rowData.status === ContractStatus.signed)))
  ) {
    return (
      <CustomTooltip
        tooltipContent={t('placement.profile.tabs.documentTab.publishedToPassport')}
        value={<img src={PassportIcon} alt="passport" />}
      />
    );
  }
  return null;
};

const cleanDocumentId = (docId: string) => {
  return docId.includes('_') ? docId.slice(docId.indexOf('_') + 1) : docId;
};

export const deriveColumns = (
  t,
  handleOnClick: (id, emailLogs) => void,
  setCredModalProps: (openCred, documentId) => void,
) =>
  [
    {
      minWidth: 260,
      ...getColumnDefinition(t, 'document'),
      renderCell: params => DocumentIconCell(params, setCredModalProps),
    },
    {
      ...getColumnDefinition(t, 'version'),
      renderCell: XGridOverflowCell()(),
    },
    {
      ...getColumnDefinition(t, 'documentId'),
      valueGetter: params => cleanDocumentId(params.value?.toString?.() ?? ''),
      renderCell: XGridOverflowCell()(),
      sortComparator: xGridNumberSort,
    },
    {
      ...getColumnDefinition(t, 'status'),
      renderCell: XGridChipCell({
        color: (params: GridCellParams) => getStatusColor(params.row),
        size: 'small',
        disabled: params => {
          return stringMatches(params.value, PlacementDocumentStatus.notSent);
        },
        renderValue: (params: GridCellParams) => {
          const status: any = params.row?.displayStatus ?? params.value;
          if (stringMatches(status, PlacementDocumentStatus.sent)) {
            return PlacementDocumentStatus.sent;
          } else {
            return status;
          }
        },
        trailingComponent: (params: GridCellParams) => displayIcon(t, params.row),
      }),
    },
    {
      ...getColumnDefinition(t, 'sent'),
      minWidth: 200,
      renderCell: DocumentsXGridOverflowCell(
        DocumentsXGridHoverView({
          attributesKey: 'emailLogs',
          label: 'Document was sent on :',
          displayValue: (item: any) => {
            return moment(item?.publishedOn).format('MM/DD/YYYY hh:mm A') ?? missingField;
          },
          handleOnClick: handleOnClick,
          leadingComponent: (params: GridCellParams) => {
            return <FiberManualRecordIcon style={{ fontSize: '8px', color: 'black', paddingRight: '3px' }} />;
          },
          trailingComponent: (params: GridCellParams, item: any) => {
            return item?.deliveryStatus === 'Error' ? (
              <ReportProblemOutlinedIcon
                style={{ color: '#AE0F1B', width: '24px', height: '24px', paddingLeft: '5px' }}
              />
            ) : null;
          },
        }),
        true,
        undefined,
        canShowPopper,
      )(
        XGridHoverBlueCell({
          displayValue: (params: GridCellParams) => {
            return `${params?.value} ${
              params.row?.emailLogs?.length > 1 ? '(' + params.row?.emailLogs?.length + ')' : ''
            }`;
          },
          canShowPopper,
          handleOnClick: handleOnClick,
          trailingComponent: (params: GridCellParams) => {
            return params.row?.emailLogs?.findIndex(lg => lg?.deliveryStatus === 'Error') !== -1 ? (
              <ReportProblemOutlinedIcon
                style={{ color: '#AE0F1B', width: '24px', height: '24px', paddingLeft: '5px' }}
              />
            ) : null;
          },
          pointer: true,
        }),
      ),
    },
    {
      ...getColumnDefinition(t, 'signed'),
      renderCell: XGridOverflowCell()(),
    },
    {
      ...getColumnDefinition(t, 'template'),
      renderCell: XGridOverflowCell()(),
    },
    {
      ...getColumnDefinition(t, 'source'),
      renderCell: XGridOverflowCell()(),
    },
    {
      minWidth: 200,
      ...getColumnDefinition(t, 'lastUpdated'),
      renderCell: XGridOverflowCell()(),
      sortComparator: xGridDateSort,
    },
  ] as GridColumns;

export const TooltipListContent = ({ tooltip }: { tooltip: IPlacementStatusRequirementTooltip }) => {
  const { t } = useTranslation();
  return (
    <React.Fragment>
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <Typography style={{ fontSize: '12px' }} variant="body1">
            {t(tooltip.message)}
          </Typography>
        </Grid>
        {tooltip.supportingList && (
          <Grid item xs={12}>
            <ListContent list={tooltip.supportingList} />
          </Grid>
        )}
      </Grid>
    </React.Fragment>
  );
};

const ListContent = ({ list }) => {
  const { t } = useTranslation();
  return (
    <ul className="unordered-list unordered-list-item">
      {list.map((item, idx) =>
        Array.isArray(item) ? (
          <ListContent list={item} />
        ) : (
          <li key={idx}>
            <Typography style={{ fontSize: '12px' }} variant="body1">
              {t(item)}
            </Typography>
          </li>
        ),
      )}
    </ul>
  );
};
